// src/pages/home/PatientHome.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import SettingsChevron from "@/sot/primitives/SettingsChevron.jsx";
import Card from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { lightTap } from "@/utils/haptics.js";
import useGeolocation from "@/hooks/useGeolocation.js";
import useUnreadNotifications from "@/hooks/useUnreadNotifications.js";
import LocationConsentDialog from "@/components/location/LocationConsentDialog.jsx";
import DoctorMap from "@/components/maps/DoctorMap.jsx";
import { getAfriMedMapStyle } from "@/constants/mapStyles.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import {
  clearAvatarSessionCache,
  readAvatarSessionCache,
  writeAvatarSessionCache,
} from "@/lib/avatarSessionCache.js";
import supabase from "@/services/supabaseClient";
import {
  mergeDistinctAppointmentSnapshots,
  listMyPastAppointmentSnapshots,
  listMyPendingAppointmentSnapshots,
  listMyUpcomingAppointmentSnapshots,
} from "@/services/patientAppointmentSnapshots.js";
import { recoverSponsorPayments } from "@/lib/payments/recovery.js";
import { listPendingMtnMomoAppointmentPayments } from "@/lib/payments/api.ts";
import { getAppointmentLifecycleBucket } from "@/services/appointmentLifecycle.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";

const DEV_PATIENT_PHOTO_KEY = "afrimed:dev:patient_profile_photo";
const MAX_POPULAR_SERVICES = 13;
const FALLBACK_POPULAR_SERVICES = [
  "General Practitioner",
  "Telemedicine",
  "Child health / Pediatrics",
  "Maternal care / Antenatal",
  "Dermatologist",
  "HIV care",
  "Hypertension",
  "TB screening",
  "Diabetes",
  "Malaria",
  "STI screening",
  "Dental",
  "Mental health",
];
const ICON_YEARS = "/icons/appointments/years_experience.svg";
const ICON_STAR = "/icons/patient/star_filled.svg";
const ICON_VERIFIED = "/icons/doctor/verified.svg";
const ICON_PENDING_VERIFICATION = "/icons/pending-verification.svg";
const ICON_NOTIFICATIONS = "/icons/doctor/home_content_media/notifications.svg";
const ICON_SETTINGS = "/icons/settings.svg";
let patientHomeCsvFacilitiesPromise = null;
let patientHomeCsvFacilitiesCache = null;
let patientHomeFacilitiesCache = null;
const PATIENT_HOME_APPOINTMENT_CACHE_TTL_MS = 5 * 60 * 1000;
const patientHomeAppointmentCache = new Map();

function readPatientHomeAppointmentCache(userId) {
  const key = String(userId || "").trim();
  if (!key) return null;
  const entry = patientHomeAppointmentCache.get(key);
  if (!entry || Date.now() - entry.cachedAt > PATIENT_HOME_APPOINTMENT_CACHE_TTL_MS) {
    patientHomeAppointmentCache.delete(key);
    return null;
  }
  return Array.isArray(entry.items) ? entry.items : null;
}

function writePatientHomeAppointmentCache(userId, items) {
  const key = String(userId || "").trim();
  if (!key || !Array.isArray(items)) return;
  patientHomeAppointmentCache.set(key, { cachedAt: Date.now(), items });
}

function isValidFacilityLatLng(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  // Skip common placeholder coordinate rows in CSV imports.
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return false;
  return true;
}

function mergePatientHomeFacilities(...collections) {
  const merged = [];
  const seen = new Set();

  collections.flat().forEach((item) => {
    const lat = Number(item?.lat);
    const lng = Number(item?.lng);
    const name = String(item?.name || "").trim();
    if (!name || !isValidFacilityLatLng(lat, lng)) return;

    const key = `${name.toLowerCase()}|${lat.toFixed(5)}|${lng.toFixed(5)}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({ ...item, lat, lng, name });
  });

  return merged;
}

function loadPatientHomeCsvFacilities() {
  if (patientHomeCsvFacilitiesCache) {
    return Promise.resolve(patientHomeCsvFacilitiesCache);
  }
  if (patientHomeCsvFacilitiesPromise) return patientHomeCsvFacilitiesPromise;

  patientHomeCsvFacilitiesPromise = fetch("/data/health_facilities_sadc.csv")
    .then((response) => {
      if (!response.ok) throw new Error("Patient Home facilities CSV is unavailable");
      return response.text();
    })
    .then((text) => {
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length <= 1) return [];

      const facilities = lines
        .slice(1)
        .map((line, index) => {
          const cols = line.split(",");
          return {
            id: `csv-${index}-${String(cols[2] || "").trim()}`,
            name: String(cols[2] || "").trim(),
            address: "",
            city: String(cols[1] || "").trim(),
            country: String(cols[0] || "").trim(),
            lat: Number(String(cols[5] || "").trim()),
            lng: Number(String(cols[6] || "").trim()),
          };
        })
        .filter((item) => isValidFacilityLatLng(item.lat, item.lng));
      patientHomeCsvFacilitiesCache = facilities;
      return facilities;
    })
    .catch((error) => {
      // A failed static fallback must not poison later visits after a transient failure.
      patientHomeCsvFacilitiesPromise = null;
      throw error;
    });

  return patientHomeCsvFacilitiesPromise;
}

function getAppointmentsLandingPath(tab, appointmentId = "") {
  const safeTab = String(tab || "").trim().toLowerCase();
  const safeAppointmentId = String(appointmentId || "").trim();

  if (safeTab === "past") {
    return safeAppointmentId
      ? `/appointments/past/${safeAppointmentId}`
      : "/appointments/past";
  }

  if (safeTab === "pending") {
    return "/appointments?tab=pending";
  }

  return "/appointments?tab=upcoming";
}

function normalizeIdentityStateStatus(value) {
  return String(value || "").toLowerCase().trim();
}

function isReviewPendingIdentityStatus(value) {
  const status = normalizeIdentityStateStatus(value);
  return [
    "admin_reviewed",
    "approved",
    "identity_submitted",
    "in_review",
    "ocr_complete",
    "ocr_completed",
    "passed",
    "pending",
    "pending_review",
    "review_pending",
  ].includes(status);
}

function isProtectedVerifiedStatus(value) {
  const status = normalizeIdentityStateStatus(value);
  return [
    "protected_verified",
    "registry_verified",
    "verified",
  ].includes(status);
}

const HOME_SERVICE_CATEGORIES = [
  {
    id: "doctors",
    icon: "/icons/services/doctor.svg",
    route: "/appointments/doctors",
    labelKey: "home.patient.serviceCategories.doctors",
    labelFallback: "Clinicians",
  },
  {
    id: "hospitals",
    icon: "/icons/appointments/hospital_Fill.svg",
    route: "/appointments/hospitals",
    labelKey: "home.patient.serviceCategories.hospitals",
    labelFallback: "Hospitals",
  },
  {
    id: "pharmacies",
    icon: "/icons/doctor/building_type/local_pharmacy.svg",
    route: "/appointments/pharmacies",
    labelKey: "home.patient.serviceCategories.pharmacies",
    labelFallback: "Pharmacies",
  },
  {
    id: "ambulances",
    icon: "/icons/ambulance.svg",
    route: "/ambulances",
    labelKey: "home.patient.serviceCategories.ambulances",
    labelFallback: "Ambulances",
  },
];

// ✅ Dev-photo override allowed ONLY on localhost dev
function canUseDevPhoto() {
  try {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host.endsWith(".local");
    return !!import.meta.env.DEV && isLocal;
  } catch {
    return false;
  }
}

function resolveDisplayName(auth) {
  const profile = auth?.profile || null;
  const user = auth?.user || null;
  const meta = user?.user_metadata || {};

  const full =
    profile?.full_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    profile?.display_name ||
    meta?.full_name ||
    meta?.name ||
    meta?.display_name ||
    (profile?.email ? profile.email.split("@")[0] : "") ||
    (user?.email ? user.email.split("@")[0] : "") ||
    "AfriMed™ user";

  return String(full).trim() || "AfriMed™ user";
}

function normalizePatientSalutation(value) {
  const raw = String(value || "").trim();
  return ["Mr.", "Mrs.", "Ms.", "Miss", "Mme.", "Prof."].includes(raw) ? raw : "";
}

function resolvePatientAddressName(auth, fallback) {
  const profile = auth?.profile || null;
  const user = auth?.user || null;
  const meta = user?.user_metadata || {};
  const salutation =
    normalizePatientSalutation(profile?.salutation) ||
    normalizePatientSalutation(meta?.salutation);
  const familyName =
    String(profile?.last_name || profile?.family_name || meta?.last_name || meta?.family_name || "").trim() ||
    (() => {
      const full = resolveDisplayName(auth);
      const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
      return parts.length > 1 ? parts.slice(-1)[0] : "";
    })();
  if (salutation && familyName) return `${salutation} ${familyName}`.trim();
  return fallback;
}

function normalizeServiceLabel(value) {
  const text = String(value || "")
    .replace(/[_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  const words = text
    .split(" ")
    .map((word) => {
      const upper = word.toUpperCase();
      if (["HIV", "TB", "STI", "ENT", "GP"].includes(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .filter(Boolean);
  return words.join(" ").trim();
}

function splitServiceValues(value) {
  const raw = String(value || "").trim();
  if (!raw) return [];
  return raw
    .split(/[,;|]/)
    .map((part) => normalizeServiceLabel(part))
    .filter(Boolean);
}

function mergeUniqueServices(...lists) {
  const seen = new Set();
  const merged = [];

  for (const list of lists) {
    const values = Array.isArray(list) ? list : [];
    for (const value of values) {
      const label = normalizeServiceLabel(value);
      if (!label) continue;
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(label);
    }
  }

  return merged;
}

function buildUserPopularServices(appointments = [], doctorById = new Map()) {
  const weights = new Map();

  const addWeighted = (label, weight = 1) => {
    const normalized = normalizeServiceLabel(label);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    const current = weights.get(key) || { label: normalized, score: 0 };
    current.score += weight;
    weights.set(key, current);
  };

  for (const row of Array.isArray(appointments) ? appointments : []) {
    splitServiceValues(row?.consultation_type).forEach((entry) => addWeighted(entry, 3));
    splitServiceValues(row?.service_name).forEach((entry) => addWeighted(entry, 3));
    splitServiceValues(row?.service_type).forEach((entry) => addWeighted(entry, 2));

    const doctor = doctorById.get(String(row?.doctor_id || "").trim()) || {};
    splitServiceValues(doctor?.specialty).forEach((entry) => addWeighted(entry, 2));
    splitServiceValues(doctor?.specialization).forEach((entry) => addWeighted(entry, 1));
  }

  return Array.from(weights.values())
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
    .map((item) => item.label);
}

function appointmentDateTimeParts(row) {
  const dateISO = row?.selectedDate || row?.when?.dateISO || "";
  const timeHHMM = row?.selectedTime || row?.when?.time || "";

  if (dateISO) {
    const local = new Date(`${dateISO}T${timeHHMM || "00:00"}:00`);
    if (!Number.isNaN(local.getTime())) {
      return {
        ms: local.getTime(),
        dateISO,
        timeHHMM,
      };
    }
  }

  const rawDate =
    row?.appointment_date ||
    (row?.selectedDate
      ? `${row.selectedDate}T${row.selectedTime || row?.when?.time || "00:00"}:00`
      : "");
  const when = new Date(rawDate || "");

  return {
    ms: when.getTime(),
    dateISO,
    timeHHMM,
  };
}

function isImportantNotification(row) {
  const text = `${String(row?.title || "")} ${String(row?.message || "")} ${String(row?.type || "")}`.toLowerCase();
  return (
    text.includes("ehr access") ||
    text.includes("requested access") ||
    text.includes("appointment confirmed") ||
    text.includes("booking confirmed") ||
    text.includes("new time") ||
    text.includes("reschedule") ||
    text.includes("accepted") ||
    text.includes("rejected") ||
    text.includes("declined")
  );
}

function notificationToastMessage(row) {
  const title = String(row?.title || "").trim();
  const message = String(row?.message || "").trim();
  return message || title || "";
}

function resolveUiLocale(languageCode) {
  if (languageCode === "sn") return "sn-ZW";
  if (languageCode === "zu") return "zu-ZA";
  return "en-GB";
}

function localizePopularServiceLabel(label, t) {
  const normalized = String(label || "").trim().toLowerCase();
  const keyMap = {
    "general practitioner": "home.patient.popularServices.labels.generalPractitioner",
    telemedicine: "home.patient.popularServices.labels.telemedicine",
    "child health / pediatrics": "home.patient.popularServices.labels.childHealthPediatrics",
    "maternal care / antenatal": "home.patient.popularServices.labels.maternalCareAntenatal",
    dermatologist: "home.patient.popularServices.labels.dermatologist",
    "hiv care": "home.patient.popularServices.labels.hivCare",
    hypertension: "home.patient.popularServices.labels.hypertension",
    "tb screening": "home.patient.popularServices.labels.tbScreening",
    diabetes: "home.patient.popularServices.labels.diabetes",
    malaria: "home.patient.popularServices.labels.malaria",
    "sti screening": "home.patient.popularServices.labels.stiScreening",
    dental: "home.patient.popularServices.labels.dental",
    "mental health": "home.patient.popularServices.labels.mentalHealth",
  };

  const key = keyMap[normalized];
  return key ? t(key, label) : label;
}

export default function PatientHome() {
  const nav = useNavigate();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const authProfile = auth.profile || null;
  const { t, languageCode } = useLanguage();
  const { isDark } = useTheme();
  const { unreadCount, unreadItems } = useUnreadNotifications(auth?.user?.id);
  const localeCode = React.useMemo(() => resolveUiLocale(languageCode), [languageCode]);

  const resolvedDisplayName = resolveDisplayName(auth);
  const fullName =
    resolvedDisplayName === "AfriMed™ user"
      ? t("home.patient.fallbackName", "AfriMed™ user")
      : resolvedDisplayName;
  const firstName =
    String(fullName).split(/\s+/)[0] || t("home.patient.fallbackName", "AfriMed™ user");
  const welcomeName = resolvePatientAddressName(auth, firstName);
  const authProfilePhotoPath = String(
    authProfile?.profile_photo_path ||
      authProfile?.profile_photo_storage_path ||
      authProfile?.photo_path ||
      authProfile?.avatar_path ||
      ""
  ).trim();
  const authProfilePhotoUrl = String(
    authProfile?.profile_image_url || authProfile?.avatar_url || ""
  ).trim();
  const cachedAvatarSrc = React.useMemo(
    () => readAvatarSessionCache({ role: "patient", userId: auth?.user?.id }),
    [auth?.user?.id]
  );
  const [rolePhotoPath, setRolePhotoPath] = React.useState(
    () => authProfilePhotoPath
  );
  const [rolePhotoUrl, setRolePhotoUrl] = React.useState(
    () => authProfilePhotoUrl
  );
  const [notificationToast, setNotificationToast] = React.useState("");
  const profilePhoto = rolePhotoUrl || authProfilePhotoUrl;
  const profilePhotoPath = rolePhotoPath || authProfilePhotoPath;
  const [resolvedAvatarSrc, setResolvedAvatarSrc] = React.useState(
    () => cachedAvatarSrc || profilePhoto || ""
  );
  // A cached signed URL is already safe to paint. Do not hide it behind a
  // second Image() probe while the background storage refresh runs.
  const immediateAvatarSrc = cachedAvatarSrc || resolvedAvatarSrc || profilePhoto || "";
  const localizedFallbackPopularServices = React.useMemo(
    () =>
      FALLBACK_POPULAR_SERVICES.map((label) =>
        localizePopularServiceLabel(label, t),
      ),
    [t],
  );

  React.useEffect(() => {
    let alive = true;

    async function hydratePatientAvatarFields() {
      if (!auth?.user?.id) {
        if (alive) {
          setRolePhotoPath("");
          setRolePhotoUrl("");
        }
        return;
      }

      try {
        const { data, error } = await supabase
          .from("patient_profiles")
          .select("*")
          .eq("user_id", auth.user.id)
          .maybeSingle();

        if (!alive) return;
        if (error || !data) {
          if (!authProfilePhotoPath && !authProfilePhotoUrl) {
            setRolePhotoPath("");
            setRolePhotoUrl("");
            clearAvatarSessionCache({ role: "patient", userId: auth.user.id });
          }
          return;
        }
        const nextPath = String(
          data.profile_photo_path ||
            data.profile_photo_storage_path ||
            data.photo_path ||
            data.avatar_path ||
            ""
        ).trim();
        const nextUrl = String(
          data.profile_image_url ||
            data.profile_photo_url ||
            data.photo_url ||
            data.avatar_url ||
            ""
        ).trim();

        setRolePhotoPath(nextPath);
        setRolePhotoUrl(nextUrl);
        if (!nextPath && !nextUrl && !authProfilePhotoPath && !authProfilePhotoUrl) {
          clearAvatarSessionCache({ role: "patient", userId: auth.user.id });
        }
      } catch {
        if (!alive) return;
        if (!authProfilePhotoPath && !authProfilePhotoUrl) {
          setRolePhotoPath("");
          setRolePhotoUrl("");
          clearAvatarSessionCache({ role: "patient", userId: auth.user.id });
        }
      }
    }

    hydratePatientAvatarFields();
    return () => {
      alive = false;
    };
  }, [auth?.user?.id, authProfilePhotoPath, authProfilePhotoUrl]);

  React.useEffect(() => {
    const nextImportant = (Array.isArray(unreadItems) ? unreadItems : []).find(isImportantNotification);
    const notificationId = String(nextImportant?.id || "").trim();
    if (!notificationId) return;

    const seenKey = `afrimed:patient-home:notification-toast:${notificationId}`;
    try {
      if (sessionStorage.getItem(seenKey)) return;
      sessionStorage.setItem(seenKey, "1");
    } catch {
      // continue without one-time suppression
    }

    setNotificationToast(
      notificationToastMessage(nextImportant) ||
        t("notifications.common.emptyBody", "You have a new notification."),
    );
  }, [t, unreadItems]);

  React.useEffect(() => {
    let alive = true;

    async function resolveAvatar() {
      let avatarUrl = cachedAvatarSrc || profilePhoto || "";
      const path = profilePhotoPath;

      if (avatarUrl && alive) {
        setResolvedAvatarSrc(avatarUrl);
      }

      if (path) {
        try {
          const storage = supabase.storage.from("patient-profile-images");
          const { data, error } = await storage.createSignedUrl(path, 60 * 60 * 24);
          if (!error && data?.signedUrl) {
            avatarUrl = data.signedUrl;
          } else {
            const { data: publicData } = storage.getPublicUrl(path);
            avatarUrl = publicData?.publicUrl || avatarUrl;
          }
        } catch {
          // ignore, use fallback chain
        }
      }

      if (canUseDevPhoto()) {
        try {
          const devPhoto = localStorage.getItem(DEV_PATIENT_PHOTO_KEY);
          if (devPhoto) avatarUrl = devPhoto;
        } catch {
          // ignore
        }
      }

      if (!alive) return;
      if (avatarUrl) {
        setResolvedAvatarSrc(avatarUrl);
        writeAvatarSessionCache({ role: "patient", userId: auth?.user?.id, src: avatarUrl });
      } else {
        setResolvedAvatarSrc("");
      }
    }

    resolveAvatar();
    return () => {
      alive = false;
    };
  }, [
    auth?.user?.id,
    cachedAvatarSrc,
    profilePhoto,
    profilePhotoPath,
  ]);

  // ✅ Auth truth must come from useAuth() only (no supabase calls inside pages)
  const isAuthed = !!auth?.user?.id;

  const appointmentTabsBase = React.useMemo(
    () => [
      {
        id: "upcoming",
        label: t("home.patient.appointments.tabs.upcoming", "Upcoming"),
      },
      {
        id: "past",
        label: t("home.patient.appointments.tabs.past", "Past"),
      },
      {
        id: "pending",
        label: t(
          "home.patient.appointments.tabs.pending",
          "Pending for approval"
        ),
      },
    ],
    [t]
  );

  // DB status → UI chip label
  const APPT_STATUS_LABEL = React.useMemo(
    () => ({
      scheduled: t("home.patient.appointments.status.pending", "Pending approval"),
      pending: t("home.patient.appointments.status.pending", "Pending approval"),
      confirmed: t("home.patient.appointments.status.confirmed", "Confirmed"),
      in_progress: t("home.patient.appointments.status.inProgress", "In progress"),
      completed: t("home.patient.appointments.status.completed", "Completed"),
      cancelled: t("home.patient.appointments.status.cancelled", "Cancelled"),
    }),
    [t],
  );

  const [activeTab, setActiveTab] = React.useState("upcoming");
  const [appointmentOwnerId, setAppointmentOwnerId] = React.useState(
    () => auth?.user?.id || "",
  );
  const [appointmentsLoading, setAppointmentsLoading] = React.useState(
    () => !readPatientHomeAppointmentCache(auth?.user?.id),
  );
  const [appointmentsError, setAppointmentsError] = React.useState("");
  const [pastHistoryState, setPastHistoryState] = React.useState(
    () => readPatientHomeAppointmentCache(auth?.user?.id) ? "ready" : "loading",
  );
  const [appointmentItems, setAppointmentItems] = React.useState(
    () => readPatientHomeAppointmentCache(auth?.user?.id) || [],
  );
  const [pendingMtnPayments, setPendingMtnPayments] = React.useState([]);
  const [popularServices, setPopularServices] = React.useState(
    localizedFallbackPopularServices,
  );

  // Neutral fallback center (AfriMed regional HQ: Cape Town)
  const DEMO_CENTER = { lat: -33.9249, lng: 18.4241 };

  const geo = useGeolocation();

  // Mini-map ref (for flyTo from the near-me button)
  const [miniMapRef, setMiniMapRef] = React.useState(null);
  const [selectedFacility, setSelectedFacility] = React.useState(null);
  const [pendingNearYouFly, setPendingNearYouFly] = React.useState(false);
  const [pendingClinicianDiscovery, setPendingClinicianDiscovery] = React.useState(false);

  const userCoords = React.useMemo(() => {
    const lat = Number(geo?.coords?.lat);
    const lng = Number(geo?.coords?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, [geo?.coords?.lat, geo?.coords?.lng]);

  const appointmentBucket = React.useCallback(
    (row) => getAppointmentLifecycleBucket(row, Date.now()),
    []
  );

  React.useEffect(() => {
    let alive = true;

    async function loadAppointmentsAndServices() {
      if (!auth?.user?.id) {
        if (!alive) return;
        setAppointmentOwnerId("");
        setAppointmentItems([]);
        setPopularServices(localizedFallbackPopularServices);
        setAppointmentsLoading(false);
        setAppointmentsError("");
        setPastHistoryState("ready");
        return;
      }

      const cachedAppointments = readPatientHomeAppointmentCache(auth.user.id);
      if (appointmentOwnerId !== auth.user.id) {
        setAppointmentOwnerId(auth.user.id);
        setAppointmentItems(cachedAppointments || []);
      }
      setPastHistoryState(cachedAppointments ? "ready" : "loading");
      if (cachedAppointments) {
        setAppointmentItems(cachedAppointments);
        setAppointmentsLoading(false);
      }

      try {
        if (!cachedAppointments) setAppointmentsLoading(true);
        setAppointmentsError("");

        // Payment recovery and service enrichment are useful, but neither is
        // allowed to hold the appointment CTA hostage on Patient Home.
        const recoveryPromise = recoverSponsorPayments({
          role: "patient",
          userId: auth?.user?.id,
        });
        void recoveryPromise
          .then((recoveryResult) => {
            if (recoveryResult?.lastError?.message) {
              console.warn(
                "[PatientHome] sponsor recovery skipped:",
                recoveryResult.lastError.message,
              );
            }
          })
          .catch(() => {});

        const toPatientHomeAppointments = (...collections) =>
          mergeDistinctAppointmentSnapshots(...collections)
            .slice()
            .sort((a, b) => appointmentDateTimeParts(a).ms - appointmentDateTimeParts(b).ms)
            .map((row) => ({
              ...row,
              doctor_name: resolvePatientFacingDoctorName(row?.doctor, row?.doctor?.name),
              doctor_avatar: row?.doctor?.avatar || "/icons/circular-avatar-placeholder.svg",
            }));

        const loadPopularServices = async (rowsForPopularity) => {
          const doctorById = new Map(
            rowsForPopularity
              .map((row) => [String(row?.doctor_id || "").trim(), row?.doctor || {}])
              .filter(([id]) => !!id),
          );
          const userPopularServices = buildUserPopularServices(rowsForPopularity, doctorById);
          const localizedMergedServices = mergeUniqueServices(
            mergeUniqueServices(userPopularServices, FALLBACK_POPULAR_SERVICES)
              .map((label) => localizePopularServiceLabel(label, t))
              .filter(Boolean),
          ).slice(0, MAX_POPULAR_SERVICES);

          if (alive && localizedMergedServices.length > 0) {
            setPopularServices(localizedMergedServices);
          }
        };

        // The Upcoming and Pending tabs determine the initial Patient Home CTA.
        // Past history carries its own clinician hydration and must not delay it.
        const upcomingRowsPromise = listMyUpcomingAppointmentSnapshots();
        const pendingRowsPromise = listMyPendingAppointmentSnapshots();
        const pastRowsPromise = listMyPastAppointmentSnapshots();
        void pastRowsPromise.catch(() => {});

        void listPendingMtnMomoAppointmentPayments()
          .then((result) => Array.isArray(result?.attempts) ? result.attempts : [])
          .then((attempts) => { if (alive) setPendingMtnPayments(attempts); })
          .catch(() => { if (alive) setPendingMtnPayments([]); });
        const [upcomingRows, pendingRows] = await Promise.all([
          upcomingRowsPromise,
          pendingRowsPromise,
        ]);
        const cachedPastRows = (cachedAppointments || []).filter(
          (row) => appointmentBucket(row) === "past",
        );
        const initialRows = toPatientHomeAppointments(
          upcomingRows || [], pendingRows || [], cachedPastRows,
        );

        if (!alive) return;
        setAppointmentItems(initialRows);
        setAppointmentsLoading(false);
        void loadPopularServices(initialRows).catch(() => {});

        void pastRowsPromise
          .then((pastRows) => {
            if (!alive) return;
            const hydratedRows = toPatientHomeAppointments(
              upcomingRows || [],
              pendingRows || [],
              pastRows || [],
            );
            setAppointmentItems(hydratedRows);
            writePatientHomeAppointmentCache(auth.user.id, hydratedRows);
            setPastHistoryState("ready");
            return loadPopularServices(hydratedRows);
          })
          .catch(() => {
            if (alive) setPastHistoryState("error");
            // Keep the already usable CTA and any account-scoped cached history.
          });
      } catch (e) {
        if (!alive) return;
        if (!cachedAppointments) {
          setAppointmentsError(
            t(
              "home.patient.appointments.error",
              "We’re loading your appointments. Please try again in a moment.",
            ),
          );
          setAppointmentItems([]);
          setPastHistoryState("error");
        }
        setAppointmentsLoading(false);
      } finally {
        // The critical appointment shell clears its loading state immediately above.
      }
    }

    loadAppointmentsAndServices();

    const onChanged = () => loadAppointmentsAndServices();
    const onFocus = () => loadAppointmentsAndServices();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadAppointmentsAndServices();
      }
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("afrimed:appointments:changed", onChanged);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("afrimed:appointments:changed", onChanged);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [auth?.user?.id, appointmentBucket, localizedFallbackPopularServices, t]);

  const resumeMtnPayment = React.useCallback((attempt) => {
    const clinicianUserId = String(attempt?.clinician_user_id || "").trim();
    const serviceId = String(attempt?.service_id || "").trim();
    const attemptId = String(attempt?.attempt_id || "").trim();
    const startsAt = String(attempt?.starts_at || "").trim();
    const start = new Date(startsAt);
    if (!clinicianUserId || !serviceId || !attemptId || Number.isNaN(start.getTime())) return;
    const pad = (value) => String(value).padStart(2, "0");
    const selectedDate = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
    const selectedTime = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
    const context = attempt?.booking_context || {};
    const bookingKey = `mtn:${attemptId}`;
    const booking = { bookingKey, doctorId: clinicianUserId, doctorUserId: clinicianUserId, serviceId, selectedServiceId: serviceId, selectedDate, selectedTime, mtnAttemptStartsAt: startsAt, paymentMethod: "mtn_momo", paymentMethodLabel: "MTN MoMo", serviceName: String(context?.service_name || "Consultation"), amountPaid: Number(attempt?.amount_minor || 0) / 100, currency: String(attempt?.currency || "ZAR"), consultationType: String(context?.consultation_type || "private"), careSubjectType: String(context?.care_subject_type || "self"), careSubjectId: String(context?.care_subject_dependant_id || ""), careSubjectName: String(context?.care_subject_name || ""), careSubjectRelationship: String(context?.care_subject_relationship || ""), careSubjectDateOfBirth: context?.care_subject_date_of_birth || null };
    try { localStorage.setItem(`afrimed:bookingKey:${bookingKey}`, JSON.stringify(booking)); } catch {}
    nav(`/appointments/payment/${clinicianUserId}?booking_key=${encodeURIComponent(bookingKey)}`, { state: booking });
  }, [nav]);

  // Facilities (DB first, CSV fallback)
  const [allFacilities, setAllFacilities] = React.useState(
    () => patientHomeFacilitiesCache || [],
  );
  const [facilitiesLoading, setFacilitiesLoading] = React.useState(
    () => !patientHomeFacilitiesCache,
  );

  // Load facilities (DB first, CSV fallback)
  React.useEffect(() => {
    let cancelled = false;

    const cachedFacilities = patientHomeFacilitiesCache;
    if (cachedFacilities) {
      setAllFacilities(cachedFacilities);
      setFacilitiesLoading(false);
    } else {
      setFacilitiesLoading(true);
    }
    let dbFacilities = [];
    let csvFacilities = [];
    const publishFacilities = () => {
      if (!cancelled) {
        const nextFacilities = mergePatientHomeFacilities(dbFacilities, csvFacilities);
        patientHomeFacilitiesCache = nextFacilities;
        setAllFacilities(nextFacilities);
      }
    };

    const dbFacilitiesPromise = supabase
      .from("healthcare_locations")
      .select("id, facility_name, address, city, country, latitude, longitude")
      .limit(2000)
      .then(({ data: dbRows, error: dbErr }) => {
        if (dbErr || !Array.isArray(dbRows)) return;
        dbFacilities = dbRows.map((row) => ({
          id: String(row?.id || `db-${row?.facility_name || ""}-${row?.latitude}-${row?.longitude}`),
          name: String(row?.facility_name || "").trim(),
          address: String(row?.address || "").trim(),
          city: String(row?.city || "").trim(),
          country: String(row?.country || "").trim(),
          lat: Number(row?.latitude),
          lng: Number(row?.longitude),
        }));
        publishFacilities();
      });

    const csvFacilitiesPromise = loadPatientHomeCsvFacilities().then((rows) => {
      csvFacilities = Array.isArray(rows) ? rows : [];
      publishFacilities();
    });

    void Promise.allSettled([dbFacilitiesPromise, csvFacilitiesPromise]).then(() => {
      if (!cancelled) setFacilitiesLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const mapFacilities = React.useMemo(() => {
    const src = Array.isArray(allFacilities) ? allFacilities : [];
    // DoctorMap handles viewport filtering; pass full loaded set.
    return src;
  }, [allFacilities]);

  const [patientIdStatus, setPatientIdStatus] = React.useState("not_started");

  React.useEffect(() => {
    let alive = true;

    async function loadVerificationStatus() {
      if (!auth?.user?.id) {
        if (alive) setPatientIdStatus("not_started");
        return;
      }

      try {
        const [profileRes, uploadRes] = await Promise.all([
          supabase
            .from("patient_profiles")
            .select("verification_status")
            .eq("user_id", auth.user.id)
            .maybeSingle(),
          supabase
            .from("id_uploads")
            .select("id,status,doc_type,created_at,path")
            .eq("user_id", auth.user.id)
            .in("doc_type", ["passport", "national_id"])
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

        const profileStatus = normalizeIdentityStateStatus(profileRes?.data?.verification_status);
        const uploadStatus = normalizeIdentityStateStatus(uploadRes?.data?.status);
        const hasUploadEvidence = Boolean(uploadRes?.data?.id || uploadRes?.data?.path);

        let resolved = "not_started";
        if (isProtectedVerifiedStatus(profileStatus)) {
          resolved = "verified";
        } else if (
          hasUploadEvidence &&
          (isReviewPendingIdentityStatus(uploadStatus) ||
            isReviewPendingIdentityStatus(profileStatus) ||
            profileStatus === "verified")
        ) {
          resolved = "review_pending";
        }

        if (alive) setPatientIdStatus(resolved);
      } catch {
        if (alive) {
          const fallback = normalizeIdentityStateStatus(
            auth?.profile?.id_verification_status || auth?.profile?.verification_status || ""
          );
          setPatientIdStatus(
            isProtectedVerifiedStatus(fallback)
              ? "verified"
              : isReviewPendingIdentityStatus(fallback)
                ? "review_pending"
                : "not_started"
          );
        }
      }
    }

    loadVerificationStatus();
    return () => {
      alive = false;
    };
  }, [auth?.user?.id, auth?.profile?.id_verification_status, auth?.profile?.verification_status]);

  const patientIsVerified = patientIdStatus === "verified";
  const showPatientVerifyNudge = !patientIsVerified;
  const patientIdentityCardCopy = React.useMemo(() => {
    if (patientIdStatus === "review_pending") {
      return {
        title: t(
          "home.patient.verifyIdentity.reviewPendingTitle",
          "Identity received. Review pending."
        ),
        body: t(
          "home.patient.verifyIdentity.reviewPendingBody",
          "OCR complete. Review pending."
        ),
        action: t("home.patient.verifyIdentity.reviewPendingAction", "View identity status"),
      };
    }

    return {
      title: t("home.patient.verifyIdentity.title", "Verify your identity"),
      body: t(
        "home.patient.verifyIdentity.body",
        "Verified profiles generate more trust."
      ),
      action: t("home.patient.verifyIdentity.enabled", "Enabled"),
    };
  }, [patientIdStatus, t]);

  const appointmentItemsForCurrentUser =
    appointmentOwnerId === (auth?.user?.id || "") ? appointmentItems : [];
  const appointmentHistoryPending =
    appointmentOwnerId !== (auth?.user?.id || "") || pastHistoryState === "loading";
  const selectedHistoryPending = activeTab === "past" && appointmentHistoryPending;
  const effectiveAppointmentsLoading =
    appointmentsLoading || appointmentOwnerId !== (auth?.user?.id || "");
  const appointmentCounts = React.useMemo(() => {
    const counts = { upcoming: 0, pending: 0, past: 0 };
    (Array.isArray(appointmentItemsForCurrentUser) ? appointmentItemsForCurrentUser : []).forEach((row) => {
      const bucket = appointmentBucket(row);
      if (bucket in counts) counts[bucket] += 1;
    });
    return counts;
  }, [appointmentItemsForCurrentUser, appointmentBucket]);

  const appointmentTabs = appointmentTabsBase.map((tab) => {
    const count = Number(appointmentCounts?.[tab.id] || 0);
    const countText = effectiveAppointmentsLoading ||
      (tab.id === "past" && appointmentHistoryPending)
      ? "…"
      : appointmentsError || (tab.id === "past" && pastHistoryState === "error")
        ? "—"
        : count;
    return {
      ...tab,
      label: `${tab.label} (${countText})`,
    };
  });

  const filteredAppointmentItems = React.useMemo(() => {
    return (Array.isArray(appointmentItemsForCurrentUser) ? appointmentItemsForCurrentUser : []).filter(
      (row) => appointmentBucket(row) === activeTab
    );
  }, [appointmentItemsForCurrentUser, appointmentBucket, activeTab]);

  const showAppointmentsAsEmpty =
    !effectiveAppointmentsLoading &&
    !selectedHistoryPending &&
    !appointmentsError &&
    !(activeTab === "past" && pastHistoryState === "error") &&
    filteredAppointmentItems.length === 0;

  const showAppointmentsCalmError =
    !effectiveAppointmentsLoading &&
    (!!appointmentsError || (activeTab === "past" && pastHistoryState === "error"));

  const handleNearYou = React.useCallback(() => {
    if (userCoords) return;
    if (geo.needsConsent) {
      setPendingNearYouFly(true);
      geo.openDialog();
      return;
    }
    if (geo.status === "denied" || geo.consent === "denied") {
      setPendingNearYouFly(false);
      return;
    }
    setPendingNearYouFly(true);
    geo.getOnce();
  }, [geo, userCoords]);

  const goToClinicianDiscovery = React.useCallback(() => {
    nav("/appointments/doctors");
  }, [nav]);

  const handleFindClinicians = React.useCallback(() => {
    lightTap();
    if (geo?.consent === "always") {
      goToClinicianDiscovery();
      return;
    }
    setPendingClinicianDiscovery(true);
    geo.openDialog();
  }, [geo, goToClinicianDiscovery]);

  const handleLocationDialogClose = React.useCallback(() => {
    setPendingClinicianDiscovery(false);
    geo.closeDialog();
  }, [geo]);

  const handleLocationAllowOnce = React.useCallback(() => {
    geo.allowOnce();
    if (pendingClinicianDiscovery) {
      setPendingClinicianDiscovery(false);
      goToClinicianDiscovery();
    }
  }, [geo, goToClinicianDiscovery, pendingClinicianDiscovery]);

  const handleLocationAllowAlways = React.useCallback(() => {
    geo.allowAlways();
    if (pendingClinicianDiscovery) {
      setPendingClinicianDiscovery(false);
      goToClinicianDiscovery();
    }
  }, [geo, goToClinicianDiscovery, pendingClinicianDiscovery]);

  const handleLocationDeny = React.useCallback(() => {
    geo.deny();
    if (pendingClinicianDiscovery) {
      setPendingClinicianDiscovery(false);
      goToClinicianDiscovery();
    }
  }, [geo, goToClinicianDiscovery, pendingClinicianDiscovery]);

  React.useEffect(() => {
    if (geo?.consent === "always" && geo?.status === "granted") {
      geo.startWatch();
      return () => geo.stopWatch();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo?.consent, geo?.status]);

  React.useEffect(() => {
    if (!pendingNearYouFly || !miniMapRef || !userCoords) return;
    miniMapRef.flyTo({
      center: [Number(userCoords.lng), Number(userCoords.lat)],
      zoom: 13,
      duration: 800,
    });
    setPendingNearYouFly(false);
  }, [miniMapRef, pendingNearYouFly, userCoords]);

  React.useEffect(() => {
    if (geo?.status === "denied" || geo?.consent === "denied") {
      setPendingNearYouFly(false);
    }
  }, [geo?.consent, geo?.status]);

  function openServiceCategory(category) {
    lightTap();
    const target =
      HOME_SERVICE_CATEGORIES.find((item) => item.id === category)?.route ||
      "/appointments/doctors";
    nav(target);
  }

  return (
    <div className="w-full">
      <main className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        {notificationToast ? (
          <div
            className="
              fixed left-1/2 -translate-x-1/2
              bottom-[92px] md:bottom-6
              w-full max-w-[720px] px-4
              z-[80]
            "
          >
            <div
              className="
                w-full rounded-[12px]
                bg-[#0B0F1A] text-white
                px-4 py-3
                flex items-center justify-between gap-3
                shadow-[0_10px_30px_rgba(2,6,23,0.35)]
              "
              role="status"
              aria-live="polite"
            >
              <div className="text-body-medium truncate">{notificationToast}</div>
              <button
                type="button"
                onClick={() => setNotificationToast("")}
                aria-label={t("common.close", "Close")}
                className="
                  shrink-0 h-9 w-9 rounded-[10px]
                  bg-[rgba(255,255,255,0.08)]
                  hover:bg-[rgba(255,255,255,0.12)]
                  flex items-center justify-center
                "
              >
                <span className="text-white text-[18px] leading-none translate-y-[-1px]">×</span>
              </button>
            </div>
          </div>
        ) : null}

        {/* Header: avatar on top, greeting underneath */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-3">
            {/* Avatar – circular, 44x44 */}
            <button
              type="button"
              onClick={() => nav("/settings/patient")}
              className="inline-flex shrink-0"
              aria-label={t("home.patient.actions.openProfile", "Open profile")}
            >
              <div
                className="
                  h-11 w-11
                  rounded-full
                  p-[1.5px]
                  bg-[rgb(var(--clr-surface-cont-highest))]
                  ring-1 ring-[rgba(24,50,119,0.12)]
                  shadow-[0_6px_12px_-4px_rgba(29,79,215,0.06),0_3px_6px_-2px_rgba(26,27,35,0.06),0_1.5px_0px_-0.5px_rgba(26,27,35,0.06)]
                "
              >
                <div
                  className="
                    relative
                    h-full w-full
                    rounded-full
                    overflow-hidden
                    bg-[rgb(var(--clr-surface-container-low))]
                  "
                >
                  <img
                    src="/icons/circular-avatar-placeholder.svg"
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                    draggable="false"
                  />
                  {immediateAvatarSrc ? (
                    <img
                      src={immediateAvatarSrc}
                      alt={fullName}
                      className="absolute inset-0 h-full w-full object-cover"
                      draggable="false"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                </div>
              </div>
            </button>

            <div className="flex items-center gap-2 md:hidden">
              <button
                type="button"
                onClick={() => nav("/settings/patient/notifications")}
                className="relative h-10 w-10 rounded-xl flex items-center justify-center"
                aria-label={t("home.patient.actions.notifications", "Notifications")}
              >
                <img src={ICON_NOTIFICATIONS} alt="" className="h-6 w-6" draggable="false" />
                {unreadCount > 1 ? (
                  <span
                    className="
                      absolute -right-1 -top-1
                      min-w-[18px] h-[18px] px-1
                      rounded-full
                      bg-[rgb(var(--schemes-critical,248,83,58))]
                      text-[10px] leading-[18px] text-white text-center font-medium
                    "
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                ) : unreadCount === 1 ? (
                  <span
                    className="
                      absolute right-[7px] top-[7px]
                      h-2.5 w-2.5 rounded-full
                      bg-[rgb(var(--schemes-critical,248,83,58))]
                    "
                  />
                ) : null}
              </button>
              <button
                type="button"
                onClick={() => nav("/settings/patient")}
                className="relative h-10 w-10 rounded-xl flex items-center justify-center"
                aria-label={t("home.patient.actions.settings", "Settings")}
              >
                <img src={ICON_SETTINGS} alt="" className="h-6 w-6" draggable="false" />
              </button>
            </div>
          </div>

          {/* Greeting below avatar */}
          <div className="mt-7">
            <div
              className="
                text-on-surface
                text-headline-medium
                tracking-[-0.2px]
                text-[28px] leading-[34px]
                md:text-[32px] md:leading-[38px]
              "
            >
              {t("home.patient.welcome", "Welcome, {firstName}!", { firstName: welcomeName })}
            </div>
          </div>
        </div>

        <div className="mb-6">
          <FolaOnboardingCard
            role="patient"
            placement="patientHome"
            onCtaClick={handleFindClinicians}
          />
        </div>

        {/* Your Appointments */}
        <section className="mb-6">
          {pendingMtnPayments.length > 0 ? (
            <div className="mb-4 space-y-3">
              {pendingMtnPayments.map((attempt) => (
                <div key={String(attempt?.attempt_id || "mtn-pending")} className="flex flex-col gap-3 border-b border-outline-variant/30 pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-title-small text-on-surface">Payment pending</div>
                    <p className="mt-1 text-body-medium text-on-surface-var">Your MTN MoMo approval is still in progress. No appointment has been created yet.</p>
                  </div>
                  <Button type="button" onClick={() => resumeMtnPayment(attempt)}>Complete payment</Button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-title-medium text-on-surface-var">
              {t("home.patient.appointments.title", "Your Appointments")}
            </h2>
            <button
              type="button"
              onClick={() => nav(getAppointmentsLandingPath(activeTab))}
              className="text-body-medium text-primary"
            >
              {t("home.patient.actions.viewAll", "View all")}
            </button>
          </div>

          {/* Appointment status pills – AfriPill row */}
          <div className="mt-3 chips-viewport -mx-1">
            <div className="chips-row px-1">
              {appointmentTabs.map((tab) => (
                <AfriPill
                  key={tab.id}
                  value={tab.id}
                  label={tab.label}
                  isActive={tab.id === activeTab}
                  onChange={(val) => {
                    lightTap();
                    setActiveTab(val);
                  }}
                  size="medium"
                  variant="home"
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="mt-4">
            {!isAuthed ? (
              <Card className="p-6">
                <div className="flex flex-col items-center text-center">
                  <img
                    src="/icons/doctor1.svg"
                    alt=""
                    className="h-10 w-10"
                    draggable="false"
                  />

                  <div className="mt-4 text-body-large text-on-surface-var">
                    {t(
                      "home.patient.appointments.empty.title",
                      "You don’t have any scheduled appointments."
                    )}
                    <br />
                    {t(
                      "home.patient.appointments.empty.body",
                      "Book your next consultation to see it here."
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => nav("/appointments/doctors")}
                    className="
                      mt-5
                      h-11 px-4 rounded-[12px]
                      bg-[var(--state-primary-12)]
                      text-label-large text-primary
                    "
                  >
                    {t("home.patient.appointments.bookAction", "Book appointment")}
                  </button>
                </div>
              </Card>
            ) : effectiveAppointmentsLoading || selectedHistoryPending ? (
              <Card className="p-4">
                <div className="text-body-medium text-on-surface-var">
                  {t("common.loading", "Loading...")}
                </div>
              </Card>
            ) : showAppointmentsCalmError ? (
              <Card className="p-4">
                <div className="text-body-medium text-on-surface-var">
                  {t(
                    "home.patient.appointments.error",
                    "We’re loading your appointments. Please try again in a moment."
                  )}
                </div>
              </Card>
            ) : filteredAppointmentItems.length > 0 ? (
              <div className="space-y-3">
                {filteredAppointmentItems.slice(0, 3).map((a) => {
                  const { dateISO, timeHHMM } = appointmentDateTimeParts(a);
                  const dateBase = dateISO ? new Date(`${dateISO}T00:00:00`) : null;
                  const dateText =
                    dateBase && !Number.isNaN(dateBase.getTime())
                      ? dateBase.toLocaleDateString(localeCode, {
                          weekday: "short",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "";
                  const timeText = timeHHMM || "";
                  const verificationStatus = normalizeVerificationStatus(
                    a?.doctor?.verificationStatus || a?.doctor?.verified_status,
                    a?.doctor?.verified ? "verified" : ""
                  );
                  const showVerifiedBadge =
                    verificationStatus === "verified" || verificationStatus === "pending";

                  return (
                    <Card
                      as="button"
                      key={a.id}
                      type="button"
                      onClick={() =>
                        nav(getAppointmentsLandingPath(activeTab, a.id))
                      }
                      className="
                        w-full text-left
                        rounded-[12px]
                        bg-[rgb(var(--clr-surface-cont-highest))]
                        shadow-[0_1px_2px_rgba(15,23,42,0.06)]
                        overflow-hidden
                      "
                      aria-label={t(
                        "home.patient.appointments.open",
                        "Open appointment",
                      )}
                    >
                      <div className="px-4 py-4 flex items-center gap-3">
                        <AvatarThumb68
                          src={a.doctor_avatar}
                          alt={
                            a.doctor_name ||
                            t(
                              "home.patient.appointments.defaults.doctor",
                              "Doctor",
                            )
                          }
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <div className="text-body-large text-on-surface truncate">
                              {a.doctor_name ||
                                t(
                                  "home.patient.appointments.defaults.appointment",
                                  "Appointment",
                                )}
                            </div>
                            {showVerifiedBadge ? (
                              <span className="inline-flex h-5 w-5 shrink-0">
                                <img
                                  src={
                                    verificationStatus === "verified"
                                      ? ICON_VERIFIED
                                      : ICON_PENDING_VERIFICATION
                                  }
                                  alt=""
                                  className="h-full w-full object-contain"
                                  draggable="false"
                                />
                              </span>
                            ) : null}
                          </div>

                          <div className="mt-0.5 text-body-medium text-on-surface-var truncate">
                            {a?.doctor?.specialty ||
                              t(
                                "home.patient.appointments.defaults.doctor",
                                "Doctor",
                              )}
                          </div>

                          <div className="mt-1 flex items-center gap-3 text-body-medium text-on-surface-var">
                            <div className="flex items-center gap-1.5">
                              <span>{a?.doctor?.yearsExperience || "—"}</span>
                              <img
                                src={ICON_YEARS}
                                alt=""
                                className="h-[18px] w-[18px]"
                                draggable="false"
                              />
                            </div>

                            <div className="flex items-center gap-1.5">
                              <span>{a?.doctor?.rating || "—"}</span>
                              <img
                                src={ICON_STAR}
                                alt=""
                                className="h-[16px] w-[16px] shrink-0 translate-y-[1px]"
                                draggable="false"
                              />
                            </div>
                          </div>

                          {dateText || timeText ? (
                            <div className="mt-1 text-body-medium text-on-surface-var">
                              {dateText}
                              {timeText ? `${dateText ? " · " : ""}${timeText}` : ""}
                            </div>
                          ) : null}

                        </div>

                        <SettingsChevron className="h-6 w-6 shrink-0 opacity-90" />
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : showAppointmentsAsEmpty ? (
              <Card className="p-6">
                <div className="flex flex-col items-center text-center">
                  <img
                    src="/icons/doctor1.svg"
                    alt=""
                    className="h-10 w-10"
                    draggable="false"
                  />

                  <div className="mt-4 text-body-large text-on-surface-var">
                    {appointmentHistoryPending ||
                    appointmentCounts.upcoming + appointmentCounts.pending + appointmentCounts.past > 0
                      ? `${appointmentTabsBase.find((tab) => tab.id === activeTab)?.label || "Appointments"} (0)`
                      : t(
                          "home.patient.appointments.empty.title",
                          "You don’t have any scheduled appointments."
                        )}
                    <br />
                    {activeTab === "upcoming"
                      ? t("home.patient.appointments.empty.body", "Book your next consultation to see it here.")
                      : null}
                  </div>

                  <button
                    type="button"
                    onClick={() => nav("/appointments/doctors")}
                    className="
                      mt-5
                      h-11 px-4 rounded-[12px]
                      bg-[var(--state-primary-12)]
                      text-label-large text-on-primary
                    "
                  >
                    {t("home.patient.appointments.bookAction", "Book appointment")}
                  </button>
                </div>
              </Card>
            ) : null}
          </div>
        </section>

        <hr className="mt-12 mb-10 border-t border-[color:rgb(var(--clr-outline-variant))]" />

        {/* Service categories */}
        <section className="mb-8">
          <h2 className="mb-3 text-body-large text-on-surface">
            {t("home.patient.serviceCategories.title", "Service categories")}
          </h2>

          <div
            className="
              -mx-1 flex gap-5 overflow-x-auto px-1 pb-2
              [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            "
          >
            {HOME_SERVICE_CATEGORIES.map((category) => {
              const label = t(category.labelKey, category.labelFallback);
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => openServiceCategory(category.id)}
                  className="flex w-[136px] shrink-0 flex-col items-center"
                >
                  <span
                    className="
                      flex h-[136px] w-[136px] items-center justify-center rounded-[12px]
                      bg-[linear-gradient(180deg,#76D1FF_0%,#52B6F4_100%)]
                      shadow-[0_0_0_1px_rgba(24,50,119,0.10),0_1.5px_0_-0.5px_rgba(26,27,35,0.06),0_3px_3px_-1px_rgba(26,27,35,0.06),0_3px_6px_-2px_rgba(26,27,35,0.06),0_6px_12px_-4px_rgba(29,79,215,0.06),0_12px_24px_-10px_rgba(29,79,215,0.06)]
                    "
                  >
                    <img
                      src={category.icon}
                      alt=""
                      className="h-[45px] w-[45px] object-contain [filter:brightness(0)_invert(1)]"
                      draggable="false"
                    />
                  </span>
                  <span className="mt-4 w-full text-center text-title-medium text-on-surface">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <hr className="mt-12 mb-10 border-t border-[color:rgb(var(--clr-outline-variant))]" />

        {/* Popular services */}
        <section className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-title-medium text-on-surface-var">
              {t("home.patient.popularServices.title", "Popular services")}
            </h2>

            <button
              type="button"
              onClick={() => nav("/appointments/doctors")}
              className="text-body-medium text-primary"
            >
              {t("home.patient.popularServices.action", "All services")}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {popularServices.map((label) => (
              <button
                key={label}
                type="button"
                onClick={() =>
                  nav(`/appointments/doctors?specialty=${encodeURIComponent(label)}`)
                }
                className="
                  px-3 py-1.5 rounded-full
                  bg-[color:var(--state-primary-12)]
                  text-label-large text-on-surface
                  whitespace-nowrap
                  hover:bg-[rgba(2,6,23,0.06)]
                "
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        {/* Healthcare near you – map card */}
        <section className="mb-8">
          <h2 className="mb-3 text-title-medium text-on-surface-var">
            {t("home.patient.nearby.title", "Healthcare near you")}
          </h2>

          <Card
            className="
              overflow-hidden
            "
          >
            <div className="relative h-64 md:h-80 lg:h-96 w-full">
              <DoctorMap
                diagnosticSurface="patient-home"
                center={userCoords || DEMO_CENTER}
                showUser={!!userCoords}
                user={userCoords}
                onFacilitySelect={(f) => {
                  if (!f) return;
                  setSelectedFacility({
                    id: f.id || null,
                    name: String(
                      f.name ||
                        t(
                          "home.patient.nearby.defaultFacilityName",
                          "Healthcare facility",
                        ),
                    ),
                    address: String(f.address || f.facility_address || "").trim(),
                    city: String(f.city || f.facility_city || "").trim(),
                    country: String(f.country || f.facility_country || "").trim(),
                    lat: Number.isFinite(Number(f.lat)) ? Number(f.lat) : null,
                    lng: Number.isFinite(Number(f.lng)) ? Number(f.lng) : null,
                  });
                }}
                onFacilityPopupClose={() => {
                  setSelectedFacility(null);
                }}
                doctors={[]}
                facilities={mapFacilities}
                heightClass="h-64 md:h-80 lg:h-96"
                frameClassName="w-full h-full overflow-hidden rounded-[12px]"
                mapStyle={getAfriMedMapStyle(isDark)}
                facilityIcon="/markers/afrimed-hospital.svg"
                facilityIconNearest="/markers/afrimed-nearest-hospital.svg"
                doctorIcon="/markers/afrimed-nearest-doctor.svg"
                userIcon="/markers/afrimed-user.svg"
                facilitySize={44}
                facilityNearestSize={64}
                doctorSize={52}
                doctorNearestSize={56}
                userSize={32}
                onMapReady={(m) => setMiniMapRef(m)}
                onRequireLocation={handleNearYou}
              />

              <button
                type="button"
                onClick={async () => {
                  if (!userCoords) {
                    try {
                      await handleNearYou();
                    } catch {
                      return;
                    }
                  }

                  const lat = userCoords?.lat;
                  const lng = userCoords?.lng;

                  if (!miniMapRef || lat == null || lng == null) return;

                  miniMapRef.flyTo({
                    center: [Number(lng), Number(lat)],
                    zoom: 13,
                    duration: 800,
                  });
                }}
                className="
                  absolute right-4 bottom-4
                  h-11 w-11 rounded-[16px]
                  bg-[rgb(var(--clr-surface-cont-highest))]
                  flex items-center justify-center
                  shadow-[0_10px_30px_rgba(15,23,42,0.18)]
                "
                aria-label={t("home.patient.nearby.action", "Find healthcare near me")}
              >
                <img
                  src="/icons/doctor/near_me_filled.svg"
                  alt=""
                  className="h-5 w-5"
                  draggable="false"
                />
              </button>
            </div>
          </Card>

          {selectedFacility?.name ? (
            <Card
              className="
                mt-2
                px-4 py-3
              "
            >
              <div className="text-label-medium text-on-surface-var mb-1">
                {t("home.patient.nearby.selectedFacility", "Selected facility")}
              </div>
              <div className="text-body-large text-on-surface">{selectedFacility.name}</div>
              {selectedFacility.address ||
              selectedFacility.city ||
              selectedFacility.country ? (
                <div className="text-body-small text-on-surface-var mt-1">
                  {[
                    selectedFacility.address,
                    [selectedFacility.city, selectedFacility.country]
                      .filter(Boolean)
                      .join(", "),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              ) : null}

              <button
                type="button"
                onClick={() => {
                  const qs = new URLSearchParams();
                  const facilityName = String(selectedFacility?.name || "").trim();
                  if (facilityName) {
                    qs.set("facilityName", facilityName);
                    qs.set("q", facilityName);
                  }
                  if (selectedFacility?.id) qs.set("facilityId", String(selectedFacility.id));
                  if (Number.isFinite(Number(selectedFacility?.lat))) {
                    qs.set("facilityLat", String(Number(selectedFacility.lat)));
                  }
                  if (Number.isFinite(Number(selectedFacility?.lng))) {
                    qs.set("facilityLng", String(Number(selectedFacility.lng)));
                  }
                  nav(`/appointments/doctors?${qs.toString()}`);
                }}
                className="mt-3 inline-flex h-10 items-center rounded-[10px] px-4 text-label-large bg-[var(--state-primary-12)] text-primary"
              >
                {t("home.patient.nearby.seeDoctors", "See clinicians at this facility")}
              </button>
            </Card>
          ) : null}

          {geo.consent === "denied" && geo.error ? (
            <div className="text-body-medium text-on-surface-var mt-2">
              {geo.error}
            </div>
          ) : null}

          {facilitiesLoading ? (
            <div className="text-body-small text-on-surface-var mt-2">
              {t("home.patient.nearby.loading", "Loading nearby facilities...")}
            </div>
          ) : null}
        </section>

        {/* Verify your identity card */}
        {showPatientVerifyNudge && (
          <section className="mt-4 mb-10">
            <Card
              as="button"
              type="button"
              onClick={() => nav("/settings/patient/p/edit-id-passport")}
              className="
                w-full text-left
                px-4 py-6
                flex items-start justify-between gap-3
              "
            >
              <div className="flex-1 min-w-0">
                <div className="text-title-medium text-on-surface mb-1">
                  {patientIdentityCardCopy.title}
                </div>
                <div className="text-body-medium text-on-surface-var mb-2">
                  {patientIdentityCardCopy.body}
                </div>

                <div className="text-label-large text-primary">
                  {patientIdentityCardCopy.action}
                </div>
              </div>

              <div className="shrink-0 flex items-start justify-center pt-[2px]">
                <img
                  src="/icons/doctor/Group.svg"
                  alt=""
                  className="h-12 w-12"
                  draggable="false"
                />
              </div>
            </Card>
          </section>
        )}

        <LocationConsentDialog
          open={geo.dialogOpen}
          onClose={handleLocationDialogClose}
          onAllowOnce={handleLocationAllowOnce}
          onAllowAlways={handleLocationAllowAlways}
          onDeny={handleLocationDeny}
        />
      </main>
    </div>
  );
}
